import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Popconfirm, Space } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import type {
  MagicFlowOption,
  WorkflowPriorityRecord,
  WorkflowTemplateOverrideRecord,
  WorkflowTemplateRecord,
} from './MagicFlowAdmin.types';

type UseMagicFlowTemplatesControllerParams = {
  orgUuid?: string;
  hasOrgSelected: boolean;
  scopedHeaders: Record<string, string | boolean>;
};

export const useMagicFlowTemplatesController = ({
  orgUuid,
  hasOrgSelected,
  scopedHeaders,
}: UseMagicFlowTemplatesControllerParams) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: workflowTemplates = [],
    isLoading: isLoadingTemplates,
    isFetching: isFetchingTemplates,
  } = useQuery({
    queryKey: ['magicflow-workflow-templates', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{ workflow_templates: WorkflowTemplateRecord[] }>(
          '/workflow_template',
          {
            headers: scopedHeaders,
            params: {
              page: 1,
              page_size: 200,
              sort_by: 'updated_at',
              sort_direction: 'desc',
            },
          }
        )
        .then((res) => res.data.workflow_templates || []),
  });

  const { data: workflowTemplateOverrides = [] } = useQuery({
    queryKey: ['magicflow-workflow-template-overrides', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{ workflow_template_overrides: WorkflowTemplateOverrideRecord[] }>(
          '/workflow_template_override',
          {
            headers: scopedHeaders,
            params: {
              page: 1,
              page_size: 200,
              sort_by: 'updated_at',
              sort_direction: 'desc',
            },
          }
        )
        .then((res) => res.data.workflow_template_overrides || []),
  });

  const { data: workflowPriorities = [] } = useQuery({
    queryKey: ['magicflow-workflow-priorities', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{
          priorities?: WorkflowPriorityRecord[];
          workflow_priorities?: WorkflowPriorityRecord[];
        }>('/workflow_priority', {
          headers: scopedHeaders,
        })
        .then(
          (res) => res.data.priorities || res.data.workflow_priorities || []
        ),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (workflowTemplateId: string) =>
      magicFlowApi.delete(`/workflow_template/${workflowTemplateId}`, {
        headers: scopedHeaders,
      }),
    onSuccess: () => {
      showToastSuccess('Workflow template deleted successfully');
      queryClient.invalidateQueries({
        queryKey: ['magicflow-workflow-templates', orgUuid],
      });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to delete workflow template'
      );
    },
  });

  const workflowTemplateTableData = useMemo(
    () =>
      workflowTemplates.map((template) => ({
        ...template,
        key: template.workflow_template_id,
      })),
    [workflowTemplates]
  );

  const templateColumns: ColumnsType<WorkflowTemplateRecord> = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 260,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 200,
      render: (value) =>
        value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            onClick={() => {
              navigate(
                `/superadmin/magicflow/templates/${record.workflow_template_id}`
              );
            }}
            icon={<EditOutlined />}
          />
          <Popconfirm
            title="Delete workflow template"
            description="This template will be soft deleted."
            onConfirm={() =>
              deleteTemplateMutation.mutate(record.workflow_template_id)
            }
            okButtonProps={{ loading: deleteTemplateMutation.isPending }}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const templateOptions: MagicFlowOption[] = useMemo(
    () =>
      workflowTemplates.map((template) => ({
        value: template.workflow_template_id,
        label: template.name,
      })),
    [workflowTemplates]
  );

  const templateOverrideOptions: MagicFlowOption[] = useMemo(
    () =>
      workflowTemplateOverrides.map((template) => ({
        value: template.workflow_template_override_id,
        label: template.name,
      })),
    [workflowTemplateOverrides]
  );

  const priorityOptions: MagicFlowOption[] = useMemo(
    () =>
      workflowPriorities.map((priority) => ({
        value: priority.key,
        label: priority.name || priority.key,
      })),
    [workflowPriorities]
  );

  return {
    workflowTemplateTableData,
    templateColumns,
    isLoadingTemplates,
    isFetchingTemplates,
    templateOptions,
    templateOverrideOptions,
    priorityOptions,
  };
};
